// The TMAX scooter and its rider, plus the parked scooters.
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { PI } from '../lib/math.js';
import { scene } from '../core/scene.js';
import { M, P, Ph, add, smooth } from '../lib/factory.js';
import { plateTex } from '../textures/signs.js';
import {
  denimTex,
  helmetTex,
  jacketTex,
  leatherTex,
  nylonTex,
  seatTex,
  treadTex,
} from '../textures/cloth.js';
import { parked } from '../world/eixample.js';
import { V } from '../world/sagrada.js';
import { limbGeo } from '../people/models.js';

export let BIKE_SCALE,
  moto,
  lean,
  bodyMat,
  blackMat,
  tireMat,
  helmetMat,
  visorMat,
  shoeMat,
  headlightMat,
  profile,
  wheels,
  riderG,
  bikeParts,
  headSpot;

export function buildScooter() {
  // side profile given as [z, y] points, extruded across the width and centred on x = 0
  profile = function profile(pts, w, bevel = 0.03, r = 0.06) {
    const g = new THREE.ExtrudeGeometry(roundedShape(pts, r), {
      depth: w,
      curveSegments: 8,
      bevelEnabled: bevel > 0,
      bevelSize: bevel,
      bevelThickness: bevel,
      bevelSegments: 4,
    });
    g.translate(0, 0, -w / 2);
    g.rotateY(-PI / 2);
    return smooth(g);
  };

  // ---------- the rider and his scooter ----------
  // a TMAX is ~2.2 m long: at this scale (a person is ~0.92 units) the bike is scaled to 0.5
  BIKE_SCALE = 0.5;
  moto = new THREE.Group();
  moto.scale.setScalar(BIKE_SCALE);
  scene.add(moto);
  lean = new THREE.Group();
  moto.add(lean);
  // Yamaha TMAX 530 (2018) style maxi-scooter: rounded extruded profiles, smooth shading, reflections
  bodyMat = Ph(0x4a5462, { roughness: 0.28, metalness: 0.7 });
  blackMat = P(0xffffff, { map: leatherTex, roughness: 0.55, metalness: 0.1 });
  tireMat = P(0xffffff, { map: treadTex, roughness: 0.9, metalness: 0, envMapIntensity: 0.25 });
  const chromeMat = P(0xdfe3e8, { roughness: 0.12, metalness: 1 }),
    rimMat = P(0x24272b, { roughness: 0.28, metalness: 0.75 }),
    accentMat = P(0x2d6fd6, { roughness: 0.25, metalness: 0.5 });
  const discMat = P(0xa3a9b0, { roughness: 0.3, metalness: 0.95 }),
    caliperMat = P(0xd4a017, { roughness: 0.3, metalness: 0.6 }),
    springMat = P(0xe8c02c, { roughness: 0.3, metalness: 0.5 });
  const seatMat = P(0xffffff, { map: seatTex, roughness: 0.7, metalness: 0, envMapIntensity: 0.4 }),
    glassMat = P(0x3a4a60, {
      roughness: 0.02,
      metalness: 0.2,
      transparent: true,
      opacity: 0.42,
      envMapIntensity: 1.5,
    });
  const jacketMat = P(0xffffff, {
      map: jacketTex,
      roughness: 0.7,
      metalness: 0,
      envMapIntensity: 0.5,
    }),
    jeansMat = P(0xffffff, { map: denimTex, roughness: 0.9, metalness: 0, envMapIntensity: 0.3 });
  helmetMat = Ph(0xffffff, { map: helmetTex, roughness: 0.25, metalness: 0.3 });
  visorMat = P(0x34465f, { roughness: 0.02, metalness: 0.85, envMapIntensity: 1.6 });
  shoeMat = P(0xffffff, { map: leatherTex, roughness: 0.5, metalness: 0.05 });
  headlightMat = M(0xeaf4ff, { emissive: 0xd8ecff, emissiveIntensity: 1, flatShading: false });
  let LT = lean;
  const L = (g, m, x, y, z) => add(g, m, x, y, z, LT);
  function limb(a, b, r, mat) {
    const d = new THREE.Vector3().subVectors(b, a),
      m = L(
        limbGeo(+(d.length() + r).toFixed(3), +(r * 1.08).toFixed(3), +(r * 0.86).toFixed(3)),
        mat,
        0,
        0,
        0,
      );
    m.position.copy(a).addScaledVector(d.clone().normalize(), -r * 0.5);
    m.quaternion.setFromUnitVectors(V(0, -1, 0), d.normalize());
    return m;
  }
  // polygon with each corner rounded by a quadratic curve
  function roundedShape(pts, r) {
    const sh = new THREE.Shape(),
      n = pts.length,
      Pt = (i) => new THREE.Vector2(...pts[(i + n) % n]);
    for (let i = 0; i < n; i++) {
      const p = Pt(i),
        a = Pt(i - 1),
        b = Pt(i + 1),
        ra = Math.min(r, p.distanceTo(a) * 0.45),
        rb = Math.min(r, p.distanceTo(b) * 0.45);
      const p1 = p.clone().add(a.clone().sub(p).normalize().multiplyScalar(ra)),
        p2 = p.clone().add(b.clone().sub(p).normalize().multiplyScalar(rb));
      if (i === 0) sh.moveTo(p1.x, p1.y);
      else sh.lineTo(p1.x, p1.y);
      sh.quadraticCurveTo(p.x, p.y, p2.x, p2.y);
    }
    sh.closePath();
    return sh;
  }
  wheels = [];
  function wheel(z, twinDisc) {
    const w = new THREE.Group();
    w.position.set(0, 0.38, z);
    lean.add(w);
    wheels.push(w);
    add(new THREE.TorusGeometry(0.3, 0.09, 18, 48).rotateY(PI / 2), tireMat, 0, 0, 0, w);
    add(new THREE.CylinderGeometry(0.235, 0.235, 0.11, 40).rotateZ(PI / 2), rimMat, 0, 0, 0, w);
    for (const sx of [-1, 1])
      add(
        new THREE.TorusGeometry(0.228, 0.01, 6, 40).rotateY(PI / 2),
        accentMat,
        sx * 0.056,
        0,
        0,
        w,
      );
    for (let k = 0; k < 5; k++) {
      const a = k * PI * 0.4;
      add(
        new RoundedBoxGeometry(0.125, 0.19, 0.05, 2, 0.02),
        chromeMat,
        0,
        Math.cos(a) * 0.12,
        Math.sin(a) * 0.12,
        w,
      ).rotation.x = a;
    }
    add(new THREE.CylinderGeometry(0.065, 0.065, 0.15, 20).rotateZ(PI / 2), chromeMat, 0, 0, 0, w);
    for (const sx of twinDisc ? [-1, 1] : [1])
      add(
        new THREE.CylinderGeometry(0.19, 0.19, 0.012, 36).rotateZ(PI / 2),
        discMat,
        sx * 0.075,
        0,
        0,
        w,
      );
    // calipers stay still while the wheel spins
    for (const sx of twinDisc ? [-1, 1] : [1])
      L(
        new RoundedBoxGeometry(0.05, 0.13, 0.09, 2, 0.02),
        caliperMat,
        sx * 0.09,
        0.38 + 0.13,
        z - 0.1,
      ).rotation.x = 0.6;
  }
  wheel(0.98, true);
  wheel(-0.82, false);
  const fender = L(
    new THREE.TorusGeometry(0.42, 0.055, 12, 40, 1.9).rotateY(-PI / 2),
    bodyMat,
    0,
    0.38,
    0.98,
  );
  fender.rotation.x = -0.5;
  // front fairing with the angular twin LED "eyes" and a DRL strip
  L(
    profile(
      [
        [0.48, 0.5],
        [1.08, 0.62],
        [1.14, 0.86],
        [1.0, 1.2],
        [0.72, 1.36],
        [0.5, 1.0],
      ],
      0.6,
      0.05,
      0.1,
    ),
    bodyMat,
    0,
    0,
    0,
  );
  L(
    profile(
      [
        [0.95, 0.9],
        [1.13, 0.86],
        [1.1, 0.96],
        [0.98, 1.02],
      ],
      0.64,
      0.02,
      0.03,
    ),
    blackMat,
    0,
    0,
    0,
  );
  for (const sx of [-1, 1]) {
    const e = L(
      new RoundedBoxGeometry(0.21, 0.055, 0.1, 2, 0.02),
      headlightMat,
      sx * 0.17,
      0.99,
      1.08,
    );
    e.rotation.set(-0.35, 0, sx * -0.28);
    const drl = L(
      new RoundedBoxGeometry(0.16, 0.022, 0.04, 2, 0.01),
      headlightMat,
      sx * 0.2,
      0.9,
      1.12,
    );
    drl.rotation.set(-0.2, 0, sx * 0.35);
    L(
      new RoundedBoxGeometry(0.04, 0.28, 0.17, 2, 0.015),
      blackMat,
      sx * 0.34,
      0.95,
      0.9,
    ).rotation.x = -0.5;
  }
  L(
    profile(
      [
        [0.98, 1.2],
        [0.72, 1.36],
        [0.46, 1.82],
        [0.58, 1.86],
      ],
      0.5,
      0.01,
      0.04,
    ),
    glassMat,
    0,
    0,
    0,
  ).castShadow = false;
  L(new RoundedBoxGeometry(0.36, 0.14, 0.12, 2, 0.03), blackMat, 0, 1.36, 0.66).rotation.x = -0.6;
  L(
    new THREE.PlaneGeometry(0.22, 0.07),
    M(0x56b7ff, { emissive: 0x56b7ff, emissiveIntensity: 1.2, flatShading: false }),
    0,
    1.415,
    0.63,
  ).rotation.x = -0.6 - PI / 2 + PI / 2;
  L(
    profile(
      [
        [0.5, 0.5],
        [0.5, 1.0],
        [0.05, 0.92],
        [-0.1, 0.5],
      ],
      0.32,
      0.03,
      0.08,
    ),
    blackMat,
    0,
    0,
    0,
  );
  for (const sx of [-1, 1])
    L(new RoundedBoxGeometry(0.2, 0.06, 0.64, 2, 0.025), blackMat, sx * 0.27, 0.5, 0.2);
  // rear body, side "boomerang" accent, seat with pillion step, tail
  L(
    profile(
      [
        [-0.1, 0.48],
        [0.05, 0.92],
        [-0.22, 1.05],
        [-0.95, 1.12],
        [-1.2, 1.02],
        [-1.12, 0.78],
        [-0.7, 0.55],
      ],
      0.5,
      0.05,
      0.12,
    ),
    bodyMat,
    0,
    0,
    0,
  );
  L(
    profile(
      [
        [-0.45, 0.62],
        [-0.8, 0.7],
        [-1.1, 0.78],
        [-1.12, 0.83],
        [-0.5, 0.76],
      ],
      0.54,
      0.015,
      0.04,
    ),
    accentMat,
    0,
    0,
    0,
  );
  L(
    profile(
      [
        [0.02, 0.98],
        [-0.12, 1.13],
        [-0.5, 1.15],
        [-0.6, 1.27],
        [-0.92, 1.31],
        [-1.04, 1.2],
        [-0.9, 1.1],
        [-0.2, 1.0],
      ],
      0.42,
      0.05,
      0.08,
    ),
    seatMat,
    0,
    0,
    0,
  );
  L(
    new RoundedBoxGeometry(0.32, 0.05, 0.06, 2, 0.02),
    M(0xff3b2f, { emissive: 0xff2a1a, emissiveIntensity: 0.8, flatShading: false }),
    0,
    1.04,
    -1.21,
  ).rotation.x = 0.5;
  L(
    new THREE.BoxGeometry(0.27, 0.19, 0.02),
    [
      M(0xf2f2f2),
      M(0xf2f2f2),
      M(0xf2f2f2),
      M(0xf2f2f2),
      M(0xf2f2f2),
      M(0xffffff, { map: plateTex('2026 JCP', true), flatShading: false }),
    ],
    0,
    0.62,
    -1.2,
  ).rotation.x = 0.3;
  L(
    new THREE.TubeGeometry(
      new THREE.CatmullRomCurve3([
        V(0.24, 1.16, -0.62),
        V(0.25, 1.2, -0.95),
        V(0, 1.23, -1.1),
        V(-0.25, 1.2, -0.95),
        V(-0.24, 1.16, -0.62),
      ]),
      40,
      0.022,
      10,
    ),
    rimMat,
    0,
    0,
    0,
  );
  // fork, swingarm with belt cover, rear shock, exhaust, handlebar and mirrors
  for (const sx of [-1, 1])
    L(
      new THREE.CylinderGeometry(0.034, 0.04, 0.72, 16),
      chromeMat,
      sx * 0.09,
      0.7,
      0.88,
    ).rotation.x = -0.45;
  L(
    profile(
      [
        [-0.05, 0.3],
        [-0.05, 0.55],
        [-0.85, 0.48],
        [-0.9, 0.32],
      ],
      0.06,
      0.02,
      0.08,
    ),
    chromeMat,
    0.17,
    0,
    0,
  );
  const helix = new THREE.Curve();
  helix.getPoint = (t, v = new THREE.Vector3()) =>
    v.set(Math.cos(t * PI * 14) * 0.05, t * 0.34, Math.sin(t * PI * 14) * 0.05);
  const shock = new THREE.Group();
  shock.position.set(-0.17, 0.5, -0.7);
  shock.rotation.x = 0.55;
  lean.add(shock);
  add(new THREE.TubeGeometry(helix, 160, 0.011, 6), springMat, 0, 0, 0, shock);
  add(
    new THREE.CylinderGeometry(0.022, 0.022, 0.4, 12).translate(0, 0.17, 0),
    chromeMat,
    0,
    0,
    0,
    shock,
  );
  const ex = L(
    new THREE.CylinderGeometry(0.085, 0.1, 0.58, 28).rotateX(PI / 2),
    P(0x34373b, { roughness: 0.25, metalness: 0.8 }),
    -0.21,
    0.5,
    -0.8,
  );
  ex.rotation.x = -0.15;
  L(new THREE.TorusGeometry(0.07, 0.018, 8, 24), chromeMat, -0.21, 0.545, -1.09);
  L(new THREE.CylinderGeometry(0.025, 0.025, 0.78, 12).rotateZ(PI / 2), blackMat, 0, 1.38, 0.48);
  for (const sx of [-1, 1]) {
    L(
      new THREE.CylinderGeometry(0.045, 0.045, 0.14, 16).rotateZ(PI / 2),
      blackMat,
      sx * 0.42,
      1.38,
      0.48,
    );
    L(new THREE.CylinderGeometry(0.012, 0.012, 0.3, 8), blackMat, sx * 0.34, 1.5, 0.66).rotation.z =
      sx * -0.4;
    L(new RoundedBoxGeometry(0.17, 0.095, 0.035, 2, 0.015), blackMat, sx * 0.42, 1.64, 0.66);
  }
  // rider (procedural fallback until the character models load)
  riderG = new THREE.Group();
  lean.add(riderG);
  LT = riderG;
  limb(V(0, 1.24, -0.38), V(0, 1.78, -0.3), 0.25, jacketMat).scale.set(1.18, 1, 0.82);
  // jacket details: collar, zip, shoulder armour, and a laptop backpack
  L(new THREE.TorusGeometry(0.13, 0.045, 10, 24).rotateX(PI / 2), jacketMat, 0, 1.97, -0.27);
  L(
    new RoundedBoxGeometry(0.03, 0.5, 0.02, 1, 0.008),
    P(0x1c1c1e, { roughness: 0.4 }),
    0,
    1.62,
    -0.07,
  ).rotation.x = -0.15;
  for (const sx of [-1, 1])
    L(
      new THREE.SphereGeometry(0.12, 16, 12).scale(1, 0.8, 1),
      P(0x2a2d33, { roughness: 0.45 }),
      sx * 0.3,
      1.82,
      -0.3,
    );
  const pack = L(
    new RoundedBoxGeometry(0.44, 0.52, 0.2, 3, 0.07),
    P(0xffffff, { map: nylonTex, roughness: 0.75, metalness: 0 }),
    0,
    1.6,
    -0.58,
  );
  pack.rotation.x = 0.12;
  L(
    new RoundedBoxGeometry(0.3, 0.18, 0.06, 2, 0.03),
    P(0x33373d, { roughness: 0.7 }),
    0,
    1.45,
    -0.7,
  ).rotation.x = 0.12;
  for (const sx of [-1, 1])
    L(
      new RoundedBoxGeometry(0.05, 0.5, 0.03, 1, 0.01),
      P(0x1c1c1e, { roughness: 0.6 }),
      sx * 0.14,
      1.68,
      -0.12,
    ).rotation.x = -0.2;
  L(new RoundedBoxGeometry(0.38, 0.14, 0.12, 2, 0.05), helmetMat, 0, 1.98, -0.06).rotation.x = 0.2;
  L(new THREE.SphereGeometry(0.27, 32, 24), helmetMat, 0, 2.1, -0.24);
  L(
    new THREE.SphereGeometry(0.276, 32, 16, PI / 2 - 0.9, 1.8, 1.15, 0.75),
    visorMat,
    0,
    2.1,
    -0.24,
  );
  L(new RoundedBoxGeometry(0.3, 0.07, 0.12, 2, 0.03), accentMat, 0, 2.3, -0.4).rotation.x = 0.5;
  for (const sx of [-1, 1]) {
    const sh = V(sx * 0.29, 1.74, -0.3),
      el = V(sx * 0.38, 1.46, -0.02),
      ha = V(sx * 0.42, 1.4, 0.44);
    limb(sh, el, 0.09, jacketMat);
    limb(el, ha, 0.08, jacketMat);
    L(new THREE.SphereGeometry(0.078, 16, 12), blackMat, ha.x, ha.y, ha.z);
    const hip = V(sx * 0.16, 1.2, -0.32),
      kn = V(sx * 0.22, 1.24, 0.22),
      ft = V(sx * 0.26, 0.62, 0.48);
    limb(hip, kn, 0.11, jeansMat);
    limb(kn, ft, 0.1, jeansMat);
    L(new RoundedBoxGeometry(0.15, 0.13, 0.32, 2, 0.05), shoeMat, ft.x, 0.57, 0.56);
  }
  LT = lean;
  // bake a set of objects into one geometry per material (relative to `rel`), splitting multi-material meshes by group
  function bakeParts(objs, rel) {
    rel.updateMatrixWorld(true);
    const inv = rel.matrixWorld.clone().invert(),
      groups = new Map(),
      m4 = new THREE.Matrix4();
    const push = (mat, g) => {
      if (!groups.has(mat)) groups.set(mat, []);
      groups.get(mat).push(g);
    };
    const strip = (src, start, count) => {
      const g = new THREE.BufferGeometry();
      for (const k of ['position', 'normal', 'uv']) {
        const a =
          src.attributes[k] ||
          new THREE.BufferAttribute(new Float32Array(src.attributes.position.count * 2), 2);
        g.setAttribute(
          k,
          new THREE.BufferAttribute(
            a.array.slice(start * a.itemSize, (start + count) * a.itemSize),
            a.itemSize,
          ),
        );
      }
      return g;
    };
    for (const ob of objs)
      ob.traverse((o) => {
        if (!o.isMesh) return;
        const g = (o.geometry.index ? o.geometry.toNonIndexed() : o.geometry.clone()).applyMatrix4(
            m4.multiplyMatrices(inv, o.matrixWorld),
          ),
          n = g.attributes.position.count;
        if (Array.isArray(o.material))
          (g.groups.length ? g.groups : [{ start: 0, count: n, materialIndex: 0 }]).forEach((gr) =>
            push(o.material[gr.materialIndex], strip(g, gr.start, gr.count)),
          );
        else push(o.material, strip(g, 0, n));
      });
    return [...groups].map(([mat, gs]) => ({ mat, geo: mergeGeometries(gs) }));
  }
  bikeParts = bakeParts(
    lean.children.filter((o) => o !== riderG && (o.isMesh || wheels.includes(o))),
    lean,
  );
  // parked scooters along the chamfers: the same bike, instanced, in assorted colours
  {
    const o = new THREE.Object3D(),
      c = new THREE.Color(),
      cols = [0xc8392e, 0xf2f0ea, 0x1d1f22, 0xb8bcc2, 0x2f6fd0, 0x4a5462];
    for (const part of bikeParts) {
      const body = part.mat === bodyMat,
        mat = body ? Ph(0xffffff, { roughness: 0.28, metalness: 0.6 }) : part.mat;
      const im = new THREE.InstancedMesh(part.geo, mat, parked.length);
      parked.forEach((p, i) => {
        o.position.set(p.x, 0, p.z);
        o.rotation.set(0, p.ry, 0);
        o.scale.setScalar(BIKE_SCALE);
        o.updateMatrix();
        im.setMatrixAt(i, o.matrix);
        if (body) im.setColorAt(i, c.set(cols[(i * 7) % cols.length]));
      });
      im.castShadow = im.receiveShadow = true;
      scene.add(im);
    }
  }
  headSpot = new THREE.SpotLight(0xfff0cc, 0, 30, 0.55, 0.5, 1.2);
  headSpot.position.set(0, 1.0, 1.1);
  headSpot.target.position.set(0, 0, 9);
  lean.add(headSpot, headSpot.target);
}
